import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import StatusPill from './StatusPill';
import { useAuth } from '../lib/auth';
import { parseNetworkCsv } from '../lib/networkImport';
import { usePlans, useContractedPlans, useNetworkPlans, useAddNetworkPlan } from '../api/hooks';
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from '../lib/aiToast';

// Pure plan-identity key — matches the server's planKey scheme so we can detect
// which imported rows are already in the network.
const planKeyFor = (payerId, planName) => `${payerId || 'unknown'}::${String(planName || '').trim().toLowerCase()}`;

// CSV import modal (gap 2.1). Parses a plan spreadsheet, previews what's new vs.
// already in the network, and bulk-adds new plans tagged source: 'csv_import'.
export default function NetworkImport({ open, onClose, practiceId = 'prac_001' }) {
  const { session } = useAuth();
  const { data: plans = [] } = usePlans();
  const { data: contractedPlans = [] } = useContractedPlans(practiceId);
  const { data: addedPlans = [] } = useNetworkPlans(practiceId);
  const addPlan = useAddNetworkPlan();
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);

  // Plan keys already in the network: contracted DB + previously-added plans.
  const knownKeys = useMemo(() => {
    const keys = new Set();
    contractedPlans.forEach((c) => {
      keys.add(planKeyFor(c.payerId, plans.find((p) => p.id === c.planId)?.planName));
    });
    addedPlans.forEach((p) => keys.add(p.planKey || planKeyFor(p.payerId, p.planName)));
    return keys;
  }, [plans, contractedPlans, addedPlans]);

  const classify = (r) => {
    if (!r.payerResolved) return 'unrecognized';
    if (knownKeys.has(planKeyFor(r.payerId, r.planName))) return 'known';
    return 'new';
  };

  const classified = useMemo(() => (rows || []).map((r) => ({ ...r, kind: classify(r) })), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const newRows = classified.filter((r) => r.kind === 'new');
  const knownRows = classified.filter((r) => r.kind === 'known');
  const badRows = classified.filter((r) => r.kind === 'unrecognized');

  const reset = () => { setRows(null); setErrors([]); setFileName(''); setImporting(false); };
  const close = () => { reset(); onClose?.(); };

  const ingest = (text, name) => {
    const { rows: parsed, errors: errs } = parseNetworkCsv(text);
    setRows(parsed); setErrors(errs); setFileName(name || '');
  };

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => ingest(String(e.target?.result || ''), file.name);
    reader.readAsText(file);
  };

  const useSample = async () => {
    try {
      const res = await fetch('/sample-network.csv');
      ingest(await res.text(), 'sample-network.csv');
    } catch (e) {
      setErrors(['Could not load the sample file.']);
    }
  };

  const doImport = async () => {
    const addedBy = session?.user?.name || 'Front desk';
    const importedAt = new Date().toISOString();
    setImporting(true);
    try {
      await Promise.all(newRows.map((r) => addPlan.mutateAsync({
        practiceId,
        payerId: r.payerId,
        planName: r.planName,
        planType: r.planType || null,
        status: r.status,
        source: 'csv_import',
        addedBy,
        evidence: { fileName, importedAt },
      })));
      toast.success(`Imported ${newRows.length} plan${newRows.length === 1 ? '' : 's'} from ${fileName || 'CSV'}`);
      close();
    } catch (e) {
      toast.error('Some plans could not be imported');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-brand-600" />Import network from CSV</DialogTitle>
          <DialogDescription>Bulk-load your existing plan spreadsheet. Imported plans are tagged as a CSV import with full provenance.</DialogDescription>
        </DialogHeader>

        {!rows ? (
          <div className="py-2">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              className="cursor-pointer rounded-xl border-2 border-dashed border-slate-200 hover:border-brand-300 hover:bg-brand-50/40 transition-colors px-6 py-10 text-center"
            >
              <UploadCloud className="h-8 w-8 mx-auto text-slate-400" />
              <div className="mt-2 text-sm text-slate-700 font-medium">Drop a CSV here, or click to choose a file</div>
              <div className="text-xs text-slate-400 mt-1">Columns: Payer, Plan, Type, Network Status, Effective Date</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            </div>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs">
              <button onClick={useSample} className="text-brand-600 font-medium hover:underline">Use sample file</button>
              <a href="/sample-network.csv" download className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700"><Download className="h-3.5 w-3.5" />Download sample</a>
            </div>
            {errors.length > 0 && <p className="mt-3 text-xs text-rose-600 text-center">{errors.join(' ')}</p>}
          </div>
        ) : (
          <div className="py-1">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="inline-flex items-center gap-1.5 text-slate-600"><FileSpreadsheet className="h-4 w-4 text-slate-400" />{fileName} · {classified.length} rows</span>
              <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"><X className="h-3 w-3" />Choose another</button>
            </div>

            <div className="flex flex-wrap gap-2 text-xs mb-3">
              <span className="pill pill-green"><CheckCircle2 className="h-3 w-3" />{newRows.length} new</span>
              {knownRows.length > 0 && <span className="pill pill-slate">{knownRows.length} already in network</span>}
              {badRows.length > 0 && <span className="pill pill-amber"><AlertTriangle className="h-3 w-3" />{badRows.length} unrecognized payer</span>}
            </div>

            <div className="dh-inset max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 bg-slate-50/70 sticky top-0">
                  <tr>{['Payer', 'Plan', 'Type', 'Status', ''].map((h, i) => <th key={i} className="font-medium px-3 py-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {classified.map((r, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${r.kind !== 'new' ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-1.5 text-slate-700">{r.payerName}</td>
                      <td className="px-3 py-1.5 text-slate-900">{r.planName}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.planType || '—'}</td>
                      <td className="px-3 py-1.5"><StatusPill status={r.status} /></td>
                      <td className="px-3 py-1.5 text-right text-[10px] text-slate-400">
                        {r.kind === 'known' ? 'in network' : r.kind === 'unrecognized' ? 'skip' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.length > 0 && <p className="mt-2 text-xs text-rose-600">{errors.join(' ')}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" disabled={!rows || newRows.length === 0 || importing} onClick={doImport}>
            {importing ? 'Importing…' : `Import ${newRows.length > 0 ? newRows.length : ''} plan${newRows.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
